




































































































import Vue from 'vue';
import { OutlinedSelect, OutlinedTextInput } from '@descomplica/vue-components';
import ProuniFileUpload from '@/components/forms/prouni/ProuniFileUpload.vue';
import { brDateToIso, formatDateString, isoToBrDate, onlyDigits } from '@/helpers/formatters';
import { isDateStringValid, isFinancialSponsorAgeValid } from '@/helpers/validators';
import { UF_LIST, toYesNoLabel } from '@/helpers/prouniOptions';
import {
  PersonalDocumentKind,
  ProuniApplication,
  ProuniDocumentType,
} from '@/store/modules/prouni/types';

const YES = 'Sim';
const NO = 'Não';

export default Vue.extend({
  name: 'ProuniPersonalDocumentsForm',
  components: { OutlinedSelect, OutlinedTextInput, ProuniFileUpload },
  props: {
    application: {
      type: Object as () => ProuniApplication | null,
      default: null,
    },
  },
  data() {
    const app = this.application || {};
    const suggestedMajority = app.birthDate ? isFinancialSponsorAgeValid(app.birthDate) : undefined;
    const isOfAge = typeof app.isOfAge === 'boolean' ? app.isOfAge : suggestedMajority;
    // If a standalone CPF document was uploaded, the ID document did not contain
    // the CPF; otherwise assume it does.
    const hasCpfDoc = (app.documents || []).some(d => d.documentType === ProuniDocumentType.cpf);
    const documentHasCpf = !hasCpfDoc;
    return {
      form: {
        docType: app.docType || PersonalDocumentKind.identity,
        rgNumber: app.rgNumber || '',
        issueDate: isoToBrDate(app.issueDate),
        issuingBody: app.issuingBody || '',
        uf: app.uf || '',
        isOfAge,
      },
      masks: { date: formatDateString, onlyDigits },
      errors: { issueDate: false },
      majorityValue: toYesNoLabel(isOfAge),
      docKindValue: '',
      documentHasCpf,
      documentHasCpfValue: toYesNoLabel(documentHasCpf),
      documentTypes: ProuniDocumentType,
      uploaded: {
        personalDoc: (app.documents || []).some(d =>
          [ProuniDocumentType.identity, ProuniDocumentType.ctps].includes(
            d.documentType as ProuniDocumentType,
          ),
        ),
        guardian: (app.documents || []).some(d => d.documentType === ProuniDocumentType.guardian),
        cpf: hasCpfDoc,
      },
    };
  },
  computed: {
    docKindOptions(): string[] {
      return [
        this.$t('prouni.personalDocuments.kindIdentity') as string,
        this.$t('prouni.personalDocuments.kindCtps') as string,
      ];
    },
    yesNoOptions(): string[] {
      return [YES, NO];
    },
    ufOptions(): string[] {
      return UF_LIST;
    },
    isMinor(): boolean {
      return this.form.isOfAge === false;
    },
    needsCpfUpload(): boolean {
      return this.documentHasCpf === false;
    },
    documentKindType(): string {
      return this.form.docType === PersonalDocumentKind.ctps
        ? ProuniDocumentType.ctps
        : ProuniDocumentType.identity;
    },
  },
  created() {
    this.docKindValue = this.docKindLabel(this.form.docType);
  },
  mounted() {
    // Emit the initial validity so revisiting a step (already filled) enables Continue.
    this.inputHandler();
  },
  methods: {
    docKindLabel(type?: string): string {
      return type === PersonalDocumentKind.ctps
        ? (this.$t('prouni.personalDocuments.kindCtps') as string)
        : (this.$t('prouni.personalDocuments.kindIdentity') as string);
    },
    docKindHandler(value: string) {
      this.form.docType =
        value === (this.$t('prouni.personalDocuments.kindCtps') as string)
          ? PersonalDocumentKind.ctps
          : PersonalDocumentKind.identity;
      this.inputHandler();
    },
    setPersonalDocDone(done: boolean) {
      this.uploaded.personalDoc = done;
      this.inputHandler();
    },
    setGuardianDone(done: boolean) {
      this.uploaded.guardian = done;
      this.inputHandler();
    },
    documentHasCpfHandler(value: string) {
      this.documentHasCpf = value === YES;
      this.inputHandler();
    },
    setCpfDone(done: boolean) {
      this.uploaded.cpf = done;
      this.inputHandler();
    },
    majorityHandler(value: string) {
      this.form.isOfAge = value === YES;
      this.inputHandler();
    },
    inputHandler() {
      this.errors.issueDate = !!this.form.issueDate && !isDateStringValid(this.form.issueDate);

      const filledRequired = [
        this.form.docType,
        this.form.rgNumber,
        this.form.issueDate,
        this.form.issuingBody,
        this.form.uf,
      ].every(Boolean);

      const guardianOk = !this.isMinor || this.uploaded.guardian;
      const cpfOk = !this.needsCpfUpload || this.uploaded.cpf;
      const isValid =
        filledRequired &&
        !this.errors.issueDate &&
        typeof this.form.isOfAge === 'boolean' &&
        this.uploaded.personalDoc &&
        guardianOk &&
        cpfOk;

      this.$emit('input', { ...this.form, issueDate: brDateToIso(this.form.issueDate) }, isValid);
    },
  },
});
